import type { Usage } from '@/types/api';

// URL citation from web search results
export interface UrlCitation {
  type: 'url_citation';
  url: string;
  title: string;
  startIndex: number;
  endIndex: number;
}

// All known event types
export const StreamEventType = {
  // Response lifecycle
  RESPONSE_CREATED: 'response.created',
  RESPONSE_IN_PROGRESS: 'response.in_progress',
  RESPONSE_COMPLETED: 'response.completed',
  RESPONSE_FAILED: 'response.failed',

  // Text streaming
  OUTPUT_TEXT_DELTA: 'response.output_text.delta',
  OUTPUT_TEXT_DONE: 'response.output_text.done',

  // Output items
  OUTPUT_ITEM_ADDED: 'response.output_item.added',
  OUTPUT_ITEM_DONE: 'response.output_item.done',

  // Content parts
  CONTENT_PART_ADDED: 'response.content_part.added',
  CONTENT_PART_DONE: 'response.content_part.done',

  // Web search
  WEB_SEARCH_CALL_IN_PROGRESS: 'response.web_search_call.in_progress',
  WEB_SEARCH_CALL_SEARCHING: 'response.web_search_call.searching',
  WEB_SEARCH_CALL_COMPLETED: 'response.web_search_call.completed',

  // Reasoning summary
  REASONING_SUMMARY_TEXT_DELTA: 'response.reasoning_summary_text.delta',
  REASONING_SUMMARY_TEXT_DONE: 'response.reasoning_summary_text.done',
} as const;

export type StreamEventTypeValue =
  (typeof StreamEventType)[keyof typeof StreamEventType];

// Base event interface (all events share these)
export interface BaseStreamEvent {
  type: string;
  sequenceNumber: number;
  conversationId: string;
}

// Response created event
export interface ResponseCreatedEvent extends BaseStreamEvent {
  type: 'response.created';
  response: {
    id: string;
    model: string;
    status: string;
  };
}

// Response in progress event
export interface ResponseInProgressEvent extends BaseStreamEvent {
  type: 'response.in_progress';
  response: {
    id: string;
    status: 'in_progress';
  };
}

// Text delta event
export interface TextDeltaEvent extends BaseStreamEvent {
  type: 'response.output_text.delta';
  itemId: string;
  outputIndex: number;
  contentIndex: number;
  delta: string;
}

// Text done event
export interface TextDoneEvent extends BaseStreamEvent {
  type: 'response.output_text.done';
  itemId: string;
  outputIndex: number;
  text: string;
}

// Response completed event
export interface ResponseCompletedEvent extends BaseStreamEvent {
  type: 'response.completed';
  response: {
    id: string;
    outputText: string;
    usage?: Usage & { reasoningTokens?: number };
    reasoningSummary?: string;
    annotations?: UrlCitation[];
  };
}

// Response failed event
export interface ResponseFailedEvent extends BaseStreamEvent {
  type: 'response.failed';
  error: {
    message: string;
    type: string;
    code?: string;
  };
}

// Web search events
export interface WebSearchCallInProgressEvent extends BaseStreamEvent {
  type: 'response.web_search_call.in_progress';
  itemId: string;
  outputIndex: number;
}

export interface WebSearchCallSearchingEvent extends BaseStreamEvent {
  type: 'response.web_search_call.searching';
  itemId: string;
  outputIndex: number;
}

export interface WebSearchCallCompletedEvent extends BaseStreamEvent {
  type: 'response.web_search_call.completed';
  itemId: string;
  outputIndex: number;
}

// Reasoning summary delta event
export interface ReasoningSummaryTextDeltaEvent extends BaseStreamEvent {
  type: 'response.reasoning_summary_text.delta';
  itemId: string;
  outputIndex: number;
  contentIndex: number;
  delta: string;
}

// Reasoning summary done event
export interface ReasoningSummaryTextDoneEvent extends BaseStreamEvent {
  type: 'response.reasoning_summary_text.done';
  itemId: string;
  outputIndex: number;
  text: string;
}

// Union of all known events
export type StreamEvent =
  | BaseStreamEvent
  | ResponseCreatedEvent
  | ResponseInProgressEvent
  | TextDeltaEvent
  | TextDoneEvent
  | ResponseCompletedEvent
  | ResponseFailedEvent
  | WebSearchCallInProgressEvent
  | WebSearchCallSearchingEvent
  | WebSearchCallCompletedEvent
  | ReasoningSummaryTextDeltaEvent
  | ReasoningSummaryTextDoneEvent;

// Streaming state
export interface StreamingState {
  status: 'idle' | 'connecting' | 'streaming' | 'completed' | 'failed';
  conversationId: string | null;
  responseId: string | null;
  textContent: string;
  model: string | null;
  usage: (Usage & { reasoningTokens?: number }) | null;
  error: { message: string; type: string; code?: string } | null;
  lastSequenceNumber: number;
  isSearchingWeb: boolean;
  isThinking: boolean;
  reasoningSummary: string;
  annotations: UrlCitation[];
}

export const initialStreamingState: StreamingState = {
  status: 'idle',
  conversationId: null,
  responseId: null,
  textContent: '',
  model: null,
  usage: null,
  error: null,
  lastSequenceNumber: 0,
  isSearchingWeb: false,
  isThinking: false,
  reasoningSummary: '',
  annotations: [],
};

// Event handler function signature
export type EventHandler<E extends StreamEvent = StreamEvent> = (
  state: StreamingState,
  event: E
) => StreamingState;
